import {
    APP_INITIALIZER,
    enableProdMode,
    importProvidersFrom,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { environment } from '@environments/environment';
import { AppComponent } from './app/app.component';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { Features } from '@shared/store/appState';
import { permissionsManagerReducer } from './app/panel/pages/permissions-manager/store/reducers/permissions-manager.reducer';
import { PermissionsManagerEffect } from './app/panel/pages/permissions-manager/store/effects/permissions-manager.effect';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AuthConfig, OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import {
    NbDatepickerModule,
    NbDialogModule,
    NbLayoutModule,
    NbMenuModule,
    NbSidebarModule,
    NbThemeModule,
    NbTimepickerModule,
    NbToastrModule,
    NbTooltipModule,
} from '@nebular/theme';
import { NbEvaIconsModule } from '@nebular/eva-icons';
import { authCodeFlowConfig } from '@config/auth.config';
import { HeadersInterceptor } from '@config/headers.interceptor';
import { AuthService } from '@auth/services/auth.service';
import { provideRouter, TitleStrategy } from '@angular/router';
import { TemplatePageTitleStrategy } from '@shared/services/template-page-title-strategy.strategy';
import { getAllowedUrls } from '@shared/endpoints';
import { routes } from './app/app.routing';
import { asyncValidatorsReducer } from '@shared/store/reducers/async-validators.reducer';
import { commonReducer } from '@shared/store/reducers/common.reducer';
import { CommonEffect } from '@shared/store/effects/common.effect';
import { AsyncValidatorsEffect } from '@shared/store/effects/async-validators.effect';
import { referralsReducer } from './app/panel/pages/referrals/store/reducers/referrals.reducer';
import { ReferralsEffect } from './app/panel/pages/referrals/store/effects/referrals.effect';
import { allTournamentsReducer } from './app/all-tournaments/store/reducers/allTournaments.reducer';
import { AllTournamentsEffect } from './app/all-tournaments/store/effects/allTournaments.effect';
import { withdrawalsReducer } from './app/panel/pages/withdrawals/store/reducers/withdrawals.reducer';
import { WithdrawalsEffect } from './app/panel/pages/withdrawals/store/effects/withdrawals.effect';
import { userManagementReducer } from './app/panel/pages/user-management/store/reducers/user-management.reducer';
import { UserManagementEffect } from './app/panel/pages/user-management/store/effects/user-management.effect';
import { NgSelectModule } from '@ng-select/ng-select';
import { RewardsReducer } from './app/panel/pages/rewards/store/reducers/rewards.reducer';
import { RewardsEffect } from './app/panel/pages/rewards/store/effects/reward.effect';
import { gamesReducer } from './app/panel/pages/games/store/reducers/games.reducer';
import { GamesEffect } from './app/panel/pages/games/store/effects/games.effect';
import { scoringSystemsReduser } from './app/panel/pages/games/store/reducers/scoring-systems.reducer';
import { ScoringSystemsEffect } from './app/panel/pages/games/store/effects/scoring-system.effect';
import { seasonsReducer } from './app/panel/pages/seasons/store/reducers/season.reducer';
import { SeasonsEffect } from './app/panel/pages/seasons/store/effects/season.effect';
import { mediaReducer } from '@shared/store/reducers/media.reducer';
import { MediaEffect } from '@shared/store/effects/media.effect';

export const authAppInitializerFactory = (authService: AuthService) => () =>
    authService.runInitialLoginSequence();

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        provideHttpClient(withInterceptorsFromDi()),
        provideAngularSvgIcon(),
        provideAnimations(),
        importProvidersFrom(
            StoreModule.forRoot({}),
            EffectsModule.forRoot([]),
            StoreModule.forFeature(Features.Referrals, referralsReducer),
            StoreModule.forFeature(
                Features.PermissionManager,
                permissionsManagerReducer
            ),
            StoreModule.forFeature(
                Features.AllTournaments,
                allTournamentsReducer
            ),
            StoreModule.forFeature(Features.Withdrawals, withdrawalsReducer),
            StoreModule.forFeature(
                Features.UserManagement,
                userManagementReducer
            ),
            StoreModule.forFeature(
                Features.AsyncValidators,
                asyncValidatorsReducer
            ),
            StoreModule.forFeature(Features.Rewards, RewardsReducer),
            StoreModule.forFeature(Features.Games, gamesReducer),
            StoreModule.forFeature(
                Features.ScoringSystems,
                scoringSystemsReduser
            ),
            StoreModule.forFeature(Features.Seasons, seasonsReducer),
            StoreModule.forFeature(Features.Media, mediaReducer),
            StoreModule.forFeature(Features.Common, commonReducer),
            EffectsModule.forFeature([CommonEffect, AsyncValidatorsEffect]),
            EffectsModule.forFeature([UserManagementEffect]),
            EffectsModule.forFeature([WithdrawalsEffect]),
            EffectsModule.forFeature([AllTournamentsEffect]),
            EffectsModule.forFeature([PermissionsManagerEffect]),
            EffectsModule.forFeature([ReferralsEffect]),
            EffectsModule.forFeature([RewardsEffect]),
            EffectsModule.forFeature([GamesEffect]),
            EffectsModule.forFeature([ScoringSystemsEffect]),
            EffectsModule.forFeature([SeasonsEffect]),
            EffectsModule.forFeature([MediaEffect]),

            StoreDevtoolsModule.instrument({
                maxAge: 25,
                logOnly: environment.production,
                autoPause: true,
            }),
            OAuthModule.forRoot({
                resourceServer: {
                    allowedUrls: getAllowedUrls(),
                    sendAccessToken: true,
                },
            }),
            NbDatepickerModule.forRoot(),
            NbTimepickerModule.forRoot(),
            NbToastrModule.forRoot(),
            NbDialogModule.forRoot(),
            NbLayoutModule,
            NbEvaIconsModule,
            NbSidebarModule.forRoot(),
            NbMenuModule.forRoot(),
            NbThemeModule.forRoot({ name: 'default' }),
            NbTooltipModule,
            NgSelectModule
        ),
        provideRouter(routes),
        { provide: AuthConfig, useValue: authCodeFlowConfig },
        { provide: OAuthStorage, useValue: localStorage },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HeadersInterceptor,
            multi: true,
        },
        {
            provide: TitleStrategy,
            useClass: TemplatePageTitleStrategy,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: authAppInitializerFactory,
            deps: [AuthService],
            multi: true,
        },
    ],
}).catch((err) => console.error(err));
